import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  RADIOLOGY_SERVICES,
  RADIOLOGY_SERVICE_LABELS,
  type RadiologyService,
} from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { LogoUploader } from "../LogoUploader";
import { ServicesCheckboxGroup } from "../ServicesCheckboxGroup";
import {
  AddressFormFields,
  EMPTY_ADDRESS,
  type AddressFormValues,
} from "../AddressFormFields";
import { TabSaveBar } from "../TabSaveBar";

type Props = {
  initial: {
    services: RadiologyService[];
    logoUrl: string | null;
    addressSame: boolean;
    address: AddressFormValues;
  };
  onSaved: () => void;
};

function hasAddress(a: AddressFormValues) {
  return a.zipCode.length === 8 && !!a.address && !!a.city;
}

function sameAddress(a: AddressFormValues, b: AddressFormValues) {
  return (
    a.zipCode === b.zipCode &&
    a.address === b.address &&
    a.addressNumber === b.addressNumber &&
    a.addressComplement === b.addressComplement &&
    a.neighborhood === b.neighborhood &&
    a.city === b.city &&
    a.state === b.state
  );
}

function sameServices<T>(a: T[], b: T[]) {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

export function RadiologyTab({ initial, onSaved }: Props) {
  const [services, setServices] = useState<RadiologyService[]>(initial.services);
  const [logo, setLogo] = useState<string | null>(initial.logoUrl);
  const [addrSame, setAddrSame] = useState(initial.addressSame);
  const [addr, setAddr] = useState<AddressFormValues>(initial.address);

  useEffect(() => {
    setServices(initial.services);
    setLogo(initial.logoUrl);
    setAddrSame(initial.addressSame);
    setAddr(initial.address);
  }, [initial.services, initial.logoUrl, initial.addressSame, initial.address]);

  const update = trpc.organization.updateRadiologyProfile.useMutation({
    onSuccess: () => {
      toast.success("Perfil Radiologia atualizado");
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Falha ao salvar"),
  });

  const dirty =
    !sameServices(services, initial.services) ||
    logo !== initial.logoUrl ||
    addrSame !== initial.addressSame ||
    !sameAddress(addr, initial.address);

  const toggleService = (s: RadiologyService) => {
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Servicos oferecidos
        </p>
        <ServicesCheckboxGroup
          options={RADIOLOGY_SERVICES}
          labels={RADIOLOGY_SERVICE_LABELS}
          selected={services}
          onToggle={toggleService}
        />
      </div>

      <LogoUploader value={logo} onChange={setLogo} label="Logo da radiologia" />

      <div className="space-y-3">
        <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Endereco
        </p>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 px-3 py-2.5 text-sm">
          <input
            type="checkbox"
            checked={addrSame}
            onChange={(e) => setAddrSame(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-zinc-700">Mesmo endereco da clinica principal</span>
        </label>
        {!addrSame && (
          <AddressFormFields
            values={addr}
            onChange={(p) => setAddr((prev) => ({ ...prev, ...p }))}
          />
        )}
      </div>

      <TabSaveBar
        dirty={dirty}
        isPending={update.isPending}
        onSave={() =>
          update.mutate({
            services,
            logoUrl: logo,
            addressSame: addrSame,
            address:
              !addrSame && hasAddress(addr)
                ? {
                    zipCode: addr.zipCode,
                    address: addr.address,
                    addressNumber: addr.addressNumber || undefined,
                    addressComplement: addr.addressComplement || undefined,
                    neighborhood: addr.neighborhood,
                    city: addr.city,
                    state: addr.state as never,
                  }
                : undefined,
          })
        }
      />
    </div>
  );
}

export { EMPTY_ADDRESS };
